import { useTranslation } from "react-i18next";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "src/components/base/dialog";
import {
    Description,
    ErrorMessage,
    Field,
    FieldGroup,
    Fieldset,
    Label,
    RequiredLabel,
} from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import { Button, PrimaryButton } from "src/components/base/button";
import React, { useEffect, useId, useState } from "react";
import { useForm } from "@tanstack/react-form";
import Form from "src/components/base/form";
import { Checkbox, CheckboxField, CheckboxGroup } from "src/components/base/checkbox";
import { ApiInternal } from "src/api/api_internal";
import { notify } from "src/components/base/toast";
import { Divider } from "src/components/base/divider";
import { CheckCircleIcon, PlusIcon, TrashIcon } from "@heroicons/react/20/solid";
import { Subheading } from "src/components/base/heading";
import { commitDraft, DomainChip, DomainTagInput } from "src/components/dialogs/domain-tag-input";

/**
 * The properties for {@link AddDomainDialog}
 */
export type AddDomainDialogProps = {
    /** Whether the dialog is open or not */
    isOpen: boolean;
    /** Callback for closing the dialog */
    onClose: () => void;
    /** Callback for submitting the form */
    onSubmit: () => void;
};

/**
 * One row in the resolver editor inside {@link AddDomainDialog}.
 */
type ResolverRow = {
    /** Stable React key for the row. */
    id: string;
    /** Resolver IP entered by the user. */
    ip: string;
    /** Optional human-readable description (e.g. "Primary DNS"). */
    description: string;
    /** Domain UUIDs for which this resolver has already been created (skip on retry). */
    completed: Record<string, true>;
    /** "pending" until submitted; "ok" / "error" after the API call resolves. */
    status: "pending" | "ok" | "error";
    /** Localised error message when `status === "error"`. */
    error?: string;
};

const newResolverRow = (id: string): ResolverRow => ({
    id,
    ip: "",
    description: "",
    completed: {},
    status: "pending",
});

/**
 * A dialog for creating one or more split DNS domains together with their resolver set in a
 * single step.
 */
export default function AddDomainDialog(props: AddDomainDialogProps) {
    const [t] = useTranslation("dialog-add-domain");
    const idPrefix = useId();
    const [chips, setChips] = useState<DomainChip[]>([]);
    const [chipDraft, setChipDraft] = useState("");
    const [chipsError, setChipsError] = useState<string | undefined>(undefined);
    const [resolvers, setResolvers] = useState<ResolverRow[]>([newResolverRow(`${idPrefix}-r0`)]);
    const [resolversError, setResolversError] = useState<string | undefined>(undefined);
    const rowCounterRef = React.useRef(1);

    const form = useForm({
        defaultValues: {
            disabled: false,
        },
        validators: {
            onSubmitAsync: async ({ value }) => {
                const pending = commitDraft(chipDraft, chips);
                if (pending.draft !== chipDraft) setChipDraft(pending.draft);

                if (pending.chips.length === 0) {
                    setChipsError(t("error.no-domains"));
                    return { fields: {} };
                }
                setChipsError(undefined);

                if (resolvers.length === 0) {
                    setResolversError(t("error.no-resolvers"));
                    return { fields: {} };
                }
                if (resolvers.some((r) => r.ip.trim().length === 0)) {
                    setResolversError(t("error.empty-resolver-ip"));
                    return { fields: {} };
                }
                setResolversError(undefined);

                // Step 1: create domains. The endpoint returns no value, so any newly created
                // domains need their UUIDs resolved via a follow-up list fetch (step 1b).
                const nextChips: DomainChip[] = [...pending.chips];
                let createdAny = false;
                for (let i = 0; i < nextChips.length; i++) {
                    if (nextChips[i].uuid) continue;

                    const res = await ApiInternal.dns.addDomainSplitDns({
                        domain: nextChips[i].name,
                        disabled: value.disabled,
                    });

                    if (res.result === "Err") {
                        const msg = res.error.domain_invalid
                            ? t("error.invalid-domain")
                            : res.error.domain_already_exists
                              ? t("error.domain-already-exists")
                              : undefined;
                        nextChips[i] = { ...nextChips[i], status: "error", error: msg };
                    } else {
                        nextChips[i] = { ...nextChips[i], status: "ok", error: undefined };
                        createdAny = true;
                    }
                    setChips([...nextChips]);
                }

                // Step 1b: resolve UUIDs for chips that just got created.
                if (createdAny && nextChips.some((c) => c.status === "ok" && !c.uuid)) {
                    const list = await ApiInternal.dns.getAllSplitDns();
                    const byName = new Map(list.list.map((d) => [d.domain, d.uuid]));
                    for (let i = 0; i < nextChips.length; i++) {
                        if (nextChips[i].status === "ok" && !nextChips[i].uuid) {
                            const uuid = byName.get(nextChips[i].name);
                            if (uuid) nextChips[i] = { ...nextChips[i], uuid };
                        }
                    }
                    setChips([...nextChips]);
                }

                // Step 2: attach each resolver row to every successfully created domain.
                const okDomainUuids = nextChips.filter((c) => c.uuid).map((c) => c.uuid as string);
                const nextResolvers: ResolverRow[] = [...resolvers];

                for (let r = 0; r < nextResolvers.length; r++) {
                    const row = nextResolvers[r];
                    let rowError: string | undefined;
                    const completed = { ...row.completed };

                    for (const domainUuid of okDomainUuids) {
                        if (completed[domainUuid]) continue;

                        const res = await ApiInternal.dns.addResolverSplitDns(domainUuid, {
                            ip: row.ip,
                            description: row.description,
                            disabled: false,
                        });

                        if (res.result === "Err") {
                            rowError = res.error.ip_invalid
                                ? t("error.invalid-ip")
                                : res.error.ip_already_exists
                                  ? t("error.ip-already-exists")
                                  : undefined;
                            break;
                        }
                        completed[domainUuid] = true;
                    }

                    const allCovered = okDomainUuids.every((u) => completed[u]);
                    nextResolvers[r] = {
                        ...row,
                        completed,
                        status: rowError ? "error" : allCovered ? "ok" : "pending",
                        error: rowError,
                    };
                    setResolvers([...nextResolvers]);
                }

                const allDomainsOk = nextChips.every((c) => c.status === "ok");
                const allResolversOk = nextResolvers.every((r) => r.status === "ok");
                if (allDomainsOk && allResolversOk) {
                    notify.success(t("toast.saved-successfully"));
                    props.onSubmit();
                    return;
                }

                return { fields: {} };
            },
        },
    });

    useEffect(() => {
        form.reset();
        setChips([]);
        setChipDraft("");
        setChipsError(undefined);
        rowCounterRef.current = 1;
        setResolvers([newResolverRow(`${idPrefix}-r0`)]);
        setResolversError(undefined);
    }, [props.isOpen]);

    const updateResolver = (id: string, patch: Partial<ResolverRow>) => {
        setResolvers((cur) => cur.map((r) => (r.id === id ? { ...r, ...patch } : r)));
        if (resolversError) setResolversError(undefined);
    };

    const addResolverRow = () => {
        const id = `${idPrefix}-r${rowCounterRef.current++}`;
        setResolvers((cur) => [...cur, newResolverRow(id)]);
    };

    const removeResolverRow = (id: string) => {
        setResolvers((cur) => cur.filter((r) => r.id !== id));
    };

    return (
        <Dialog onClose={props.onClose} open={props.isOpen} size={"xl"}>
            <Form onSubmit={form.handleSubmit}>
                <DialogTitle>{t("heading.add-split-dns-bundle")}</DialogTitle>
                <DialogBody>
                    <Fieldset>
                        <FieldGroup>
                            <Field>
                                <RequiredLabel>{t("label.domains")}</RequiredLabel>
                                <Description>{t("description.domains-input")}</Description>
                                <DomainTagInput
                                    chips={chips}
                                    onChange={(c) => {
                                        setChips(c);
                                        if (chipsError) setChipsError(undefined);
                                    }}
                                    draft={chipDraft}
                                    onDraftChange={(d) => {
                                        setChipDraft(d);
                                        if (chipsError) setChipsError(undefined);
                                    }}
                                    placeholder={"example.com"}
                                    removeAriaLabel={t("button.remove-domain")}
                                    autoFocus={true}
                                />
                                {chipsError && <ErrorMessage>{chipsError}</ErrorMessage>}
                                {chips.some((c) => c.status === "error") && (
                                    <ErrorMessage>{t("error.some-domains-failed")}</ErrorMessage>
                                )}
                            </Field>
                            <Divider />
                            <div className={"flex flex-col gap-2"}>
                                <div className={"flex items-center justify-between"}>
                                    <Subheading>{t("label.resolvers")}</Subheading>
                                    <Button type={"button"} plain={true} onClick={addResolverRow}>
                                        <PlusIcon />
                                        {t("button.add-resolver-row")}
                                    </Button>
                                </div>
                                {resolversError && <ErrorMessage>{resolversError}</ErrorMessage>}
                                <div className={"divide-y divide-zinc-950/5 dark:divide-white/5"}>
                                    <div
                                        className={
                                            "grid grid-cols-[1fr_1fr_auto] items-center gap-3 pb-1.5 text-xs font-medium text-zinc-500 dark:text-zinc-400"
                                        }
                                    >
                                        <span>
                                            {t("label.ip")}
                                            <span className={"ml-0.5 text-red-500"}>*</span>
                                        </span>
                                        <span>{t("label.description")}</span>
                                        <span className={"w-7"} />
                                    </div>
                                    {resolvers.map((row) => (
                                        <ResolverRowFields
                                            key={row.id}
                                            row={row}
                                            descriptionPlaceholder={t("placeholder.description")}
                                            removeLabel={t("button.remove-resolver-row")}
                                            canRemove={resolvers.length > 1}
                                            onChange={(patch) => updateResolver(row.id, patch)}
                                            onRemove={() => removeResolverRow(row.id)}
                                        />
                                    ))}
                                </div>
                            </div>
                            <Divider />
                            <form.Field name={"disabled"}>
                                {(fieldApi) => (
                                    <Field>
                                        <CheckboxGroup>
                                            <CheckboxField>
                                                <Checkbox
                                                    checked={fieldApi.state.value}
                                                    onChange={(e) => fieldApi.handleChange(e)}
                                                />
                                                <Label>{t("label.add-as-disabled")}</Label>
                                            </CheckboxField>
                                        </CheckboxGroup>
                                    </Field>
                                )}
                            </form.Field>
                        </FieldGroup>
                    </Fieldset>
                </DialogBody>
                <DialogActions>
                    <Button onClick={props.onClose}>{t("button.cancel")}</Button>
                    <PrimaryButton type={"submit"}>{t("button.add")}</PrimaryButton>
                </DialogActions>
            </Form>
        </Dialog>
    );
}

/**
 * Properties for {@link ResolverRowFields}.
 */
type ResolverRowFieldsProps = {
    /** The row's current state. */
    row: ResolverRow;
    /** Placeholder shown in the description input. */
    descriptionPlaceholder: string;
    /** Aria/title text for the trash button. */
    removeLabel: string;
    /** Whether the trash button should render. */
    canRemove: boolean;
    /** Patches the row state. */
    onChange: (patch: Partial<ResolverRow>) => void;
    /** Removes the row from the parent's list. */
    onRemove: () => void;
};

/**
 * Renders one editable resolver row (IP + description + optional remove button).
 *
 * @param props see {@link ResolverRowFieldsProps}
 * @returns the rendered row
 */
function ResolverRowFields(props: ResolverRowFieldsProps) {
    const { row, descriptionPlaceholder, removeLabel, canRemove, onChange, onRemove } = props;
    const isOk = row.status === "ok";

    return (
        <div className={isOk ? "opacity-60" : ""}>
            <div className={"grid grid-cols-[1fr_1fr_auto] items-center gap-3 py-1.5"}>
                <Input
                    required={true}
                    maxLength={255}
                    placeholder={"1.1.1.1"}
                    value={row.ip}
                    onChange={(e) => onChange({ ip: e.target.value })}
                    invalid={row.status === "error"}
                    disabled={isOk}
                />
                <Input
                    maxLength={1024}
                    placeholder={descriptionPlaceholder}
                    value={row.description}
                    onChange={(e) => onChange({ description: e.target.value })}
                    disabled={isOk}
                />
                <div className={"flex w-7 shrink-0 items-center justify-end"}>
                    {isOk && <CheckCircleIcon className={"size-5 text-green-600"} aria-hidden={true} />}
                    {!isOk && canRemove && (
                        <Button
                            type={"button"}
                            plain={true}
                            aria-label={removeLabel}
                            title={removeLabel}
                            onClick={onRemove}
                        >
                            <TrashIcon />
                        </Button>
                    )}
                </div>
            </div>
            {row.error && <ErrorMessage className={"pb-1.5"}>{row.error}</ErrorMessage>}
        </div>
    );
}
